import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AdminAuthProvider } from './auth/AdminAuthContext';
import { AuthProvider } from './auth/AuthContext';
import { RequireAdminAuth } from './auth/RequireAdminAuth';
import { RequireAuth } from './auth/RequireAuth';
import { ToastProvider } from './components/ui/ToastContainer';
import { DashboardPage } from './pages/DashboardPage';
import { LoginPage } from './pages/LoginPage';
import { SessionHistoryPage } from './pages/SessionHistoryPage';
import { SettingsPage } from './pages/SettingsPage';
import { SignupPage } from './pages/SignupPage';
import { StreamingPage } from './pages/StreamingPage';
import { StreamingProfilesPage } from './pages/StreamingProfilesPage';
import { AdminActiveSessionsPage } from './pages/admin/ActiveSessionsPage';
import { AdminAuditLogsPage } from './pages/admin/AuditLogsPage';
import { AdminDashboardPage } from './pages/admin/DashboardPage';
import { AdminLoginPage } from './pages/admin/LoginPage';
import { AdminPendingPerformersPage } from './pages/admin/PendingPerformersPage';
import { AdminPerformerDetailPage } from './pages/admin/PerformerDetailPage';
import { AdminPerformersPage } from './pages/admin/PerformersPage';
import { AdminServersPage } from './pages/admin/ServersPage';
import { AdminSessionsHistoryPage } from './pages/admin/SessionsHistoryPage';
import { AdminTrainingDataUploadPage } from './pages/admin/TrainingDataUploadPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <BrowserRouter>
          <AuthProvider>
            <AdminAuthProvider>
              <Routes>
              {/* Performer */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route
                path="/dashboard"
                element={
                  <RequireAuth>
                    <DashboardPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/streaming"
                element={
                  <RequireAuth>
                    <StreamingPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/sessions"
                element={
                  <RequireAuth>
                    <SessionHistoryPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/streaming-profiles"
                element={
                  <RequireAuth>
                    <StreamingProfilesPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/settings"
                element={
                  <RequireAuth>
                    <SettingsPage />
                  </RequireAuth>
                }
              />

              {/* Admin */}
              <Route path="/admin/login" element={<AdminLoginPage />} />
              <Route
                path="/admin/dashboard"
                element={
                  <RequireAdminAuth>
                    <AdminDashboardPage />
                  </RequireAdminAuth>
                }
              />
              <Route
                path="/admin/performers/pending"
                element={
                  <RequireAdminAuth>
                    <AdminPendingPerformersPage />
                  </RequireAdminAuth>
                }
              />
              <Route
                path="/admin/performers"
                element={
                  <RequireAdminAuth>
                    <AdminPerformersPage />
                  </RequireAdminAuth>
                }
              />
              <Route
                path="/admin/performers/:ulid"
                element={
                  <RequireAdminAuth>
                    <AdminPerformerDetailPage />
                  </RequireAdminAuth>
                }
              />
              <Route
                path="/admin/sessions/active"
                element={
                  <RequireAdminAuth>
                    <AdminActiveSessionsPage />
                  </RequireAdminAuth>
                }
              />
              <Route
                path="/admin/sessions"
                element={
                  <RequireAdminAuth>
                    <AdminSessionsHistoryPage />
                  </RequireAdminAuth>
                }
              />
              <Route
                path="/admin/audit-logs"
                element={
                  <RequireAdminAuth>
                    <AdminAuditLogsPage />
                  </RequireAdminAuth>
                }
              />
              <Route
                path="/admin/servers"
                element={
                  <RequireAdminAuth>
                    <AdminServersPage />
                  </RequireAdminAuth>
                }
              />
              <Route
                path="/admin/training-data"
                element={
                  <RequireAdminAuth>
                    <AdminTrainingDataUploadPage />
                  </RequireAdminAuth>
                }
              />
              <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />

              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </AdminAuthProvider>
          </AuthProvider>
        </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  );
}
